import { Button, Spinner, type ButtonVariant } from '../ui';
import { useQueueFlow } from './flowContext';
import { useCopy } from '../strings/useCopy';
import type { QueueCandidate } from './model';

/**
 * The queue button over a card's corner — last in the stack, under Watch later and Save to playlist
 * (`listing/CardActions.tsx` holds the recipe they share). On the feed grids, the playlist page and
 * the history page, which is every grid of videos the app draws bar the queue's own: there a card
 * carries a Remove for its exact row instead.
 *
 * **One control with two faces.** A video the queue does not hold gets **Add to queue**; a video it
 * already holds gets **Remove from queue**, in the same corner, one press either way. The state a
 * card is in is worth showing rather than making the reader remember, and the alternative — an Add
 * that keeps adding — makes a grid a place where a queue quietly fills with the same video.
 *
 * **The Add never asks.** The video goes on the **end** of the list, every time, and the press that
 * puts a video somewhere else is a press on the video itself, where the reader is choosing rather
 * than collecting. Both faces of this button are one press, which is what a control repeated forty
 * times to a page has to be.
 *
 * Removing takes **every** copy of the video (`core/queue/model.ts`), because a card names a video
 * and cannot point at one of two identical rows. Picking rows off individually is the watch panel's
 * job, and its rows carry the keys to do it with.
 *
 * **Which list the press edits is what the queue is.** A list of the reader's own is this browser tab's,
 * so a press costs no request and the button draws with no session at all — the one per-card control in
 * the app that does. A queue that is a window on a playlist is that playlist, so both faces send a
 * request to it, wear an in-flight face while it is out and can be refused.
 *
 * **A list that will take neither act carries no button**, which is the rule Watch later and Save to
 * playlist keep in the same corner: a `PL…` mirror, another account's list or no session leaves the
 * corner one glyph shorter instead of forty buttons dimmed over a state the panel already shows.
 * `queueActs` (`core/queue/model.ts`) is what answers it, per face rather than in general.
 *
 * It costs nothing to draw forty of these: the flow behind them is mounted once for the whole app
 * (`QueueActionsHost.tsx`), and a queue entry is built from the card's own fields.
 */
export function AddToQueue({
  item,
  tabIndex,
  variant = 'media',
}: {
  /** The card's own row, which already carries every field a queue entry needs (`queue/model.ts`). */
  readonly item: QueueCandidate;
  readonly tabIndex?: number;
  /**
   * `media` is the corner over a thumbnail, which is where all but one caller puts this button and
   * why it is the default. The exception is a video linked inside a description or a comment
   * (`richText/RichText.tsx`): there the button sits in a paragraph rather than on a picture, and
   * `media`'s scrim would put a dark plate in the middle of a sentence.
   *
   * The **act is the same either way** — one press, onto the end of the list, the same two faces —
   * so this is a prop rather than a second component. Nothing else about the button changes with it.
   */
  readonly variant?: ButtonVariant;
}) {
  const words = useCopy().text.queue.add;
  const flow = useQueueFlow();
  // `null` on /embed, the one screen rendered outside the shell — see `useQueueFlow`.
  if (flow === null) return null;
  const queued = flow.holds(item.videoId);
  // The face this card would wear decides which act has to be available, and a face the list refuses
  // draws nothing at all. Both answers come from one `queueActs` reading (`useQueueActions.ts`).
  if (!(queued ? flow.acts.remove : flow.acts.add)) return null;
  const busy = flow.busyOf(item.videoId);
  return (
    <Button
      variant={variant}
      size="sm"
      icon
      label={busy ? words.working : queued ? words.remove(item.title) : words.label(item.title)}
      /* The act alone as the hover word; the accessible name above names the video, which a screen
         reader has no card to look at for. The Watch later clock's pairing, exactly. */
      title={busy ? words.working : queued ? words.removeTitle : words.title}
      tabIndex={tabIndex}
      // A second press while the first is on the wire would send a second `POST`, and §7i's duplicates
      // are accepted — so the playlist would really hold the video twice.
      disabled={busy}
      onClick={() => (queued ? flow.remove(item) : flow.add(item))}
    >
      {busy ? <Spinner /> : <QueueGlyph queued={queued} />}
    </Button>
  );
}

/**
 * A play triangle with a sign beside it: **+** to put the video in the queue, **−** to take it out.
 * One picture in two states, so the corner does not change shape when the answer changes — only the
 * bar the reader is being offered.
 *
 * The triangle is what says *queue*: this list plays. Deliberately not a list with a plus, which is
 * `AddToPlaylist`'s picture and sits directly above this one in the same stack — two glyphs a stack
 * apart that both read "add to a list" would make the pair a coin toss.
 */
function QueueGlyph({ queued }: { readonly queued: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path d="M3.5 4.5v12l10-6z" fill="currentColor" />
      {/* The sign's bars: the upright is dropped for the minus, so the two states share a stroke. */}
      <path
        d="M14.5 17h7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {queued ? null : (
        <path
          d="M18 13.5v7"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}
